// Matching a candidate's Bullhorn "Qualifications" to the credentials a recruiter can request.
//
// The credential list is Bullhorn's Certification categories (id + name). A candidate's Qualifications come back
// either as those same records (so the ids match exactly) or as plain names, depending on how the Bullhorn
// instance is configured, so matching tries the id first and then the name.

// "C&G 2391 - Inspection & Testing" and "C&G 2391 – Inspection and Testing" are the same credential: compare
// names on their letters and digits only, ignoring case, spacing, punctuation and "&" vs "and".
export const squash = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');

/**
 * @param qualifications [{ id: string|null, name }]  what the candidate has in Bullhorn
 * @param categories     [{ id, name }]                the credentials that can be requested
 * @returns { matchedIds, matched, unmatched }  matchedIds are unique category ids, in the order the qualifications are listed
 */
export function matchQualifications(qualifications, categories) {
  const byId = new Map();
  const byName = new Map();
  for (const category of categories || []) {
    if (category?.id == null) continue;
    byId.set(String(category.id), category);
    const key = squash(category.name);
    if (key && !byName.has(key)) byName.set(key, category);
  }

  const matched = [];
  const unmatched = [];
  const seen = new Set();
  for (const qualification of qualifications || []) {
    const name = String(qualification?.name ?? '').trim();
    if (!name && qualification?.id == null) continue;
    const category =
      (qualification.id != null && byId.get(String(qualification.id))) || byName.get(squash(name)) || null;
    if (!category) {
      unmatched.push(name || `#${qualification.id}`);
      continue;
    }
    if (seen.has(String(category.id))) continue;
    seen.add(String(category.id));
    matched.push({ qualification, category });
  }
  return { matchedIds: matched.map((m) => String(m.category.id)), matched, unmatched };
}

/** Existing selection first (untouched), then anything newly matched. Never unticks or reorders what is already ticked. */
export function addToSelection(selectedIds, matchedIds) {
  const current = (selectedIds || []).map(String);
  const have = new Set(current);
  return [...current, ...(matchedIds || []).map(String).filter((id) => !have.has(id))];
}

export const DEFAULT_LABEL = 'Qualifications';

/** What this client calls the field in Bullhorn ("Qualifications", "Credentials", ...); our wording only if Bullhorn gave none. */
export const labelOrDefault = (label) => {
  const text = typeof label === 'string' ? label.replace(/\s+/g, ' ').trim() : '';
  return text || DEFAULT_LABEL;
};

/**
 * One-line, human summary of a pull, e.g. "Ticked 5 of 6 from the candidate's Credentials: AM2, PASMA. Not in the
 * credentials list: Own Test Kit." The label is the client's own and is used as-is: nothing here pluralises it.
 */
export function summarisePull({ total, matched, unmatched, alreadyTicked = 0, label }) {
  const field = labelOrDefault(label);
  if (total === 0) return { tone: 'info', text: `This candidate has nothing recorded in ${field} in Bullhorn.` };
  if (matched.length === 0) {
    return { tone: 'warn', text: `None of the ${total} ${total === 1 ? 'entry' : 'entries'} in ${field} match a credential in the list: ${list(unmatched)}.` };
  }
  const names = list(matched.map((m) => m.category.name.replace(/^\s+/, '')));
  const fresh = matched.length - alreadyTicked;
  let text = `Ticked ${fresh} of ${total} from the candidate's ${field}`;
  if (alreadyTicked > 0) text += ` (${alreadyTicked} already ticked)`;
  text += `: ${names}.`;
  if (unmatched.length) text += ` Not in the credentials list: ${list(unmatched)}.`;
  return { tone: unmatched.length ? 'warn' : 'ok', text };
}

function list(names, max = 6) {
  const shown = names.slice(0, max).join(', ');
  return names.length > max ? `${shown} and ${names.length - max} more` : shown;
}
